import QRCode from "qrcode";
import { qrPayload } from "./qrPayload";
import { drawerNumber, renderDrawerBarcode } from "./drawerBarcode";

export const COMPARTMENT_QR_IMAGE = {
    width: 720,
    maxNameFontSize: 392,
};

// #209: A4 portrait at 300 dpi for signs above pallet places.
export const ZONE_SIGN_IMAGE = {
    width: 2480,
    height: 3508,
    margin: 150,
    gap: 150,
    maxNameFontSize: 640,
    maxNameHeight: 1300,
};

// "Zone" as a word of its own: "Zone 4", "ZONE-4", "Zone4", "Halle B Zone 2",
// but not "Palettenzone" or "Zonenlager".
const ZONE_WORD = /(^|[^\p{L}])zone(?!\p{L})/iu;

export function isZoneCompartmentName(name) {
    return ZONE_WORD.test(String(name || ""));
}

const QUIET_ZONE_MODULES = 4;

function drawQrMatrix(ctx, payload, x, y, maxSize) {
    const qr = QRCode.create(payload, { errorCorrectionLevel: "M" });
    const matrixSize = qr.modules.size;
    const totalModules = matrixSize + QUIET_ZONE_MODULES * 2;
    const cellSize = Math.max(1, Math.floor(maxSize / totalModules));
    const renderedSize = totalModules * cellSize;
    const originX = x + Math.floor((maxSize - renderedSize) / 2);
    const originY = y + Math.floor((maxSize - renderedSize) / 2);

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(originX, originY, renderedSize, renderedSize);
    ctx.fillStyle = "#000000";

    for (let row = 0; row < matrixSize; row += 1) {
        for (let col = 0; col < matrixSize; col += 1) {
            if (!qr.modules.data[row * matrixSize + col]) continue;
            ctx.fillRect(
                originX + (col + QUIET_ZONE_MODULES) * cellSize,
                originY + (row + QUIET_ZONE_MODULES) * cellSize,
                cellSize,
                cellSize
            );
        }
    }
}

function wrapName(ctx, name, maxWidth) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return ["Fach"];
    if (words.length === 1) {
        const word = words[0];
        if (ctx.measureText(word).width <= maxWidth) return [word];
        const midpoint = Math.ceil(word.length / 2);
        return [word.slice(0, midpoint), word.slice(midpoint)];
    }

    const lines = [];
    let current = words[0];
    for (const word of words.slice(1)) {
        const candidate = `${current} ${word}`;
        if (ctx.measureText(candidate).width <= maxWidth || lines.length === 1) {
            current = candidate;
        } else {
            lines.push(current);
            current = word;
        }
    }
    lines.push(current);
    return lines;
}

function fitName(ctx, name, maxWidth, maxHeight, maxFontSize = COMPARTMENT_QR_IMAGE.maxNameFontSize) {
    for (let fontSize = maxFontSize; fontSize >= 52; fontSize -= 4) {
        ctx.font = `700 ${fontSize}px Inter, Arial, sans-serif`;
        const lines = wrapName(ctx, name, maxWidth);
        const lineHeight = fontSize * 1.08;
        if (
            lines.length <= 2 &&
            lines.every((line) => ctx.measureText(line).width <= maxWidth) &&
            lines.length * lineHeight <= maxHeight
        ) {
            return { fontSize, lineHeight, lines };
        }
    }

    ctx.font = "700 52px Inter, Arial, sans-serif";
    const fallback = name.trim() || "Fach";
    let shortened = fallback;
    while (shortened.length > 1 && ctx.measureText(`${shortened}…`).width > maxWidth) {
        shortened = shortened.slice(0, -1);
    }
    return { fontSize: 52, lineHeight: 56, lines: [`${shortened}…`] };
}

function drawArrow(ctx, top) {
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 44;
    ctx.lineCap = "square";
    ctx.lineJoin = "miter";
    ctx.beginPath();
    ctx.moveTo(22, top + 158);
    ctx.lineTo(676, top + 158);
    ctx.moveTo(540, top + 22);
    ctx.lineTo(676, top + 158);
    ctx.lineTo(540, top + 294);
    ctx.stroke();
}

export function compartmentQrFilename(name, id) {
    const slug = (name || "fach")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || "fach";
    const suffix = String(id || "qr").slice(-8);
    return `platzwart-fach-${slug}-${suffix}.png`;
}

function measureBlock(ctx, fitted) {
    const metrics = fitted.lines.map(line => ctx.measureText(line));
    const ascent = Math.max(...metrics.map(metric => metric.actualBoundingBoxAscent ?? fitted.fontSize * 0.8));
    const descent = Math.max(...metrics.map(metric => metric.actualBoundingBoxDescent ?? fitted.fontSize * 0.2));
    return { ascent, height: (fitted.lines.length - 1) * fitted.lineHeight + ascent + descent };
}

function drawName(ctx, fitted, centerX, top, ascent) {
    ctx.fillStyle = "#000000";
    ctx.font = `700 ${fitted.fontSize}px Inter, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    fitted.lines.forEach((line, index) => {
        ctx.fillText(line, centerX, top + ascent + index * fitted.lineHeight);
    });
}

/**
 * A4 sign for a pallet place: compartment name on top, QR code below.
 */
function renderZoneSign(canvas, ctx, payload, label) {
    const { width, height, margin, gap, maxNameFontSize, maxNameHeight } = ZONE_SIGN_IMAGE;
    canvas.width = width;
    canvas.height = height;
    const contentWidth = width - margin * 2;
    const fitted = fitName(ctx, label, contentWidth, maxNameHeight, maxNameFontSize);
    const block = measureBlock(ctx, fitted);

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    drawName(ctx, fitted, width / 2, margin, block.ascent);

    // The QR fills the remaining area; its own quiet zone stays inside qrSize.
    // Whole pixels keep module edges crisp.
    const qrTop = Math.ceil(margin + block.height + gap);
    const available = height - margin - qrTop;
    const qrSize = Math.min(contentWidth, available);
    drawQrMatrix(
        ctx,
        payload,
        Math.floor((width - qrSize) / 2),
        qrTop + Math.floor((available - qrSize) / 2),
        qrSize
    );
}

/**
 * Build a high-resolution PNG for printing.
 * Regular compartments: portrait label for external label software
 * (large compartment name, right arrow, QR code).
 * Compartments named "Zone …": A4 sign (name on top, QR code below).
 */
export function createCompartmentQrImage({ id, name, scan_number }) {
    if (!id) throw new Error("Fach-ID fehlt.");

    const number = drawerNumber(name);
    if (number !== null) {
        return {
            ...renderDrawerBarcode(number, scan_number), payload: scan_number, variant: "drawer",
            filename: compartmentQrFilename(name, id).replace("platzwart-fach-", "platzwart-schublade-"),
        };
    }

    const payload = qrPayload("compartment", id);
    const canvas = document.createElement("canvas");
    const label = String(name || "Fach");
    if (isZoneCompartmentName(label)) {
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Bild konnte nicht erzeugt werden.");
        renderZoneSign(canvas, ctx, payload, label);
        return {
            payload,
            variant: "a4",
            dataUrl: canvas.toDataURL("image/png"),
            filename: compartmentQrFilename(label, id),
            width: canvas.width,
            height: canvas.height,
        };
    }

    canvas.width = COMPARTMENT_QR_IMAGE.width;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Bild konnte nicht erzeugt werden.");

    const fitted = fitName(ctx, label, canvas.width, 848);
    // No additional outer padding. Keep the four-module QR quiet zone.
    const block = measureBlock(ctx, fitted);
    const nameTop = 0;
    const arrowTop = Math.ceil(block.height + 70);
    const qrTop = arrowTop + 316 + 32;
    const qrSize = canvas.width;
    canvas.height = qrTop + qrSize;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawName(ctx, fitted, canvas.width / 2, nameTop, block.ascent);
    drawArrow(ctx, arrowTop);
    drawQrMatrix(ctx, payload, 0, qrTop, qrSize);

    return {
        payload,
        variant: "label",
        dataUrl: canvas.toDataURL("image/png"),
        filename: compartmentQrFilename(label, id),
        width: canvas.width,
        height: canvas.height,
    };
}
